/**
 * Jovii — WHAT THE BROWSE LAYER IS LOOKING AT.
 *
 * Exactly one thing on a browse screen is "focused" at a time: a poster, a
 * banner, a resume card. It is drawn lifted, a little larger than its
 * neighbours and inside a light rim, and after it has been focused for a few
 * seconds it is what a trailer plays in. One idea, one owner, one answer —
 * because two components each deciding they were the focused one is exactly the
 * shape of the 268px offset and the 91px overflow.
 *
 * ── FOCUS MEANS TWO DIFFERENT THINGS AND THAT IS DELIBERATE ─────────────────
 *
 * WITH A MOUSE, focus is what the pointer is over, or what the keyboard has
 * tabbed to. That is what a person with a pointer means by "this one".
 *
 * WITH A THUMB there is no pointer, so focus is THE LEFTMOST THING ON SCREEN in
 * the row nearest the middle of it. A phone browses by scrolling, a row is read
 * from its left edge, and — the part that settled it — a focused card grows
 * into a 16:9 trailer, which needs room to grow into. To the right of the
 * leftmost card there is always a whole viewport of it.
 *
 * Two rules stop the touch case feeling like a slot machine, and both live in
 * `focus-rules.ts` where they can be tested without a phone:
 *
 *  1. **It settles, it does not chase.** The answer is only recomputed once
 *     scrolling has been quiet for `SETTLE_MS`.
 *  2. **Hysteresis on both axes.** A challenger must be two thirds on screen to
 *     take the focus and the incumbent keeps it until it is under two fifths;
 *     another ROW must be `HYSTERESIS_PX` nearer the middle to take it at all.
 *
 * ── A SCROLL DOES NOT CLEAR THE FOCUS. IT PAUSES IT. ────────────────────────
 *
 * This used to write `null` on every `scroll` and every `resize` and recompute
 * `SETTLE_MS` later. It reads well — "a flick is not a choice" — and it is why
 * the banner's trailer never started on the owner's iPhone. The dwell that
 * starts a trailer needs five uninterrupted seconds of focus, and every scroll
 * event took the focus away and started it again; measured on the live site at
 * 390x844, nudging the page 18 px every two seconds pushed the trailer out from
 * 6 s to never (docs/TRAILERS.md § C2). Safari's URL bar collapsing fires
 * `resize` too, so a real phone is worse than that test.
 *
 * So the focus SURVIVES a scroll and `moving` goes true instead. Whoever is
 * counting a dwell banks what it has and stops the clock (`dwell.ts`), and the
 * answer is re-read once the screen is quiet. Attention is measured, not
 * stillness.
 */

import { flushSync } from 'svelte';
import { movingFocus, remoteDriving } from './dpad';
import { SETTLE_MS, leftmostId, pressStands, settleNudge, type FocusRect } from './focus-rules';

export {
  HOLD_VISIBLE,
  HYSTERESIS_PX,
  SETTLE_MS,
  TAKE_VISIBLE,
  leftmostId,
  tapVerdict,
  type FocusRect,
  type TapContext,
  type TapVerdict,
  type Viewport
} from './focus-rules';

/* ────────────────────────────────────────────────────────────────────────────
 * The live registry
 * ──────────────────────────────────────────────────────────────────────────── */

export type FocusSource = 'pointer' | 'keyboard' | 'centre';

/**
 * The one focused thing, readable from any component.
 *
 * `moving` is true from the first `scroll` or `resize` until the screen has been
 * quiet for `SETTLE_MS`. It is not a focus state — the focus is unchanged while
 * it is true — it is the signal that says "this is not attention, do not count
 * it", which is what `dwell.ts` and every trailer on the page respect.
 */
export const focusState = $state<{ id: string | null; source: FocusSource; moving: boolean }>({
  id: null,
  source: 'centre',
  moving: false
});

/**
 * THE SETTLED ANSWER — the focus as of the last moment the screen was still.
 *
 * The live answer (`focusState.id`) moves WITH a fling, and the owner loves
 * that: the ring and the lift track the thumb. But everything downstream of a
 * focus change that COSTS LAYOUT — the details strip's 0fr→1fr reveal (a
 * layout animation of the whole page below it), the strip's content swap, the
 * cert chip appearing in the caption, the numeral-slide re-measure — used to
 * ride the live answer too, so a fling down Home ran two 240ms
 * grid-template-rows animations per rail it passed, kept layout dirty the
 * whole way, and turned every innocent read on the scroll path
 * (`window.scrollY` in scrolled.svelte.ts, `scrollLeft` in Rail.measure, the
 * recompute's own rects) into a forced synchronous layout of a 6,800px page.
 * Measured on the throttled phone profile: ~20ms per read, several reads per
 * frame — the choppiness the owner reported.
 *
 * So the expensive consumers key on THIS id instead: it equals the live id
 * whenever the screen is quiet, and while the screen moves it simply holds,
 * catching up in one step at the settle. The lift dances live; the strip
 * hands over once. (docs/PERF-SCROLL.md has the traces.)
 */
export const settledFocus = $state<{ id: string | null }>({ id: null });

/**
 * EVERY WRITE IS DEFERRED BY ONE MICROTASK, and it is not an optimisation.
 *
 * `focusout` fires SYNCHRONOUSLY while Svelte is removing the DOM — press a
 * focused card and the router tears the rail down with the pointer still
 * inside it — and a state write during that commit is `state_unsafe_mutation`,
 * which killed the page. A microtask is after the commit and before the next
 * frame, so nothing is visibly late, and microtasks are FIFO so an enter
 * immediately followed by a leave still resolves in that order.
 *
 * The `=== id` guard in each caller is what makes a deferred clear safe: by the
 * time it runs somebody else may legitimately hold the focus, and this must not
 * take it from them.
 */
function write(next: string | null, source: FocusSource): void {
  queueMicrotask(() => {
    // Anything that moves the focus somewhere else ends a tap's hold on it —
    // a hover, an arrow key, a sheet clearing the screen.
    if (next !== pressedId) pressedId = null;
    if (focusState.id === next) return;
    focusState.id = next;
    focusState.source = source;
    // A quiet screen has no reason to lag: hover, keyboard and taps land in
    // the settled answer immediately. Only a moving screen holds it back.
    if (!focusState.moving) settledFocus.id = next;
  });
}

/** True when this id is the focused one. Cheap; every card may call it. */
export function isFocused(id: string): boolean {
  return focusState.id === id;
}

/** True while the screen is being scrolled or resized. See `focusState.moving`. */
export function pageMoving(): boolean {
  return focusState.moving;
}

/** Deferred for the same reason every focus write is — see `write()`. */
function setMoving(next: boolean): void {
  if (focusState.moving === next) return;
  queueMicrotask(() => {
    focusState.moving = next;
    // The settle: whatever the live answer is now becomes the settled one.
    // (The settle's own recompute writes a frame later and lands through
    // `write`, where a quiet screen updates the settled id directly.)
    if (!next && settledFocus.id !== focusState.id) settledFocus.id = focusState.id;
  });
}

/** Give up the focus, e.g. when a sheet opens over the whole screen. */
export function clearFocus(): void {
  write(null, 'centre');
}

const nodes = new Map<string, HTMLElement>();

/*
 * WHICH CANDIDATES ARE ANYWHERE NEAR THE GLASS, kept live by one shared
 * IntersectionObserver so the per-frame recompute does not have to ask.
 *
 * A warm Home registers 500+ tiles (thirteen rails, ring windows grown, plates,
 * banner), and the live scroll tracking read `getBoundingClientRect` on every
 * one of them every frame — ~1.5ms a frame of pure measuring on a phone before
 * a single interesting rect was found. Yet the answer can only ever be a tile
 * that is substantially ON screen (`TAKE_VISIBLE` is two thirds), so anything
 * beyond half a viewport away cannot win and there is no reason to measure it.
 *
 * The observer's margin (half a viewport each side) is deliberately far looser
 * than the floors: an element the floors could accept is always inside it, so
 * pruning by it can never change the answer — it only skips rects the
 * arithmetic would have discarded. The incumbent and a held press are always
 * measured regardless (their HOLD test must see them leave). Observer entries
 * arrive at rendering steps, so during a hard fling the set can run a frame
 * behind; the floors' hysteresis absorbs that, and the settle recompute runs
 * long after the entries have caught up.
 */
const onGlass = new Set<string>();
const visIds = new WeakMap<Element, string>();
let visIO: IntersectionObserver | null | undefined;

function visObserver(): IntersectionObserver | null {
  if (visIO !== undefined) return visIO;
  if (typeof IntersectionObserver !== 'function') {
    visIO = null; // recompute falls back to measuring the whole registry
    return visIO;
  }
  visIO = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const id = visIds.get(entry.target);
        if (id === undefined) continue;
        if (entry.isIntersecting) onGlass.add(id);
        else onGlass.delete(id);
      }
    },
    { rootMargin: '50%' }
  );
  return visIO;
}

/**
 * A DELIBERATE TAP, and how long its answer stands.
 *
 * On a touch screen the first tap on a card focuses it instead of opening it
 * (`tapVerdict` in `focus-rules.ts`), which puts a HUMAN choice into a system
 * whose ambient answer is "the leftmost card of the middle row". The two must
 * not fight: without a hold, the very next settle recomputes the ambient
 * answer and yanks the strip off the card somebody just chose — measured as a
 * 90 ms flicker from the tapped card to the head of its row.
 *
 * So a tapped card KEEPS the focus until one of three things happens:
 *   • the focus is written somewhere else (a hover, an arrow key — see
 *     `write`), or
 *   • the card leaves the screen past the same `HOLD_VISIBLE` floor every
 *     incumbent is held to (`pressStands`), or
 *   • its node is torn down.
 * A mere scroll that keeps the card on screen does not take a choice away.
 */
let pressedId: string | null = null;

/** A tap chose this card: make it the one answer, whatever mode we are in. */
export function focusByPress(id: string): void {
  pressedId = id;
  write(id, 'pointer');
}

/**
 * Pointer mode is decided ONCE per page, from the media query, and not from the
 * last event seen. A tablet with a trackpad attached is a pointer device all
 * afternoon; a phone never becomes one. Re-reading it per event is how a single
 * stray `pointermove` from a scroll gesture turns a phone into a desktop.
 */
function pointerDriven(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

let mode: 'pointer' | 'centre' | null = null;
function currentMode(): 'pointer' | 'centre' {
  if (mode === null) mode = pointerDriven() ? 'pointer' : 'centre';
  return mode;
}

/* ── Centre mode: one listener for the whole page, not one per card ───────── */

let watching = false;
let settleTimer: ReturnType<typeof setTimeout> | null = null;

function recompute(): void {
  /**
   * ONCE A REMOTE IS DRIVING, THE AMBIENT ANSWER STANDS DOWN.
   *
   * "The leftmost thing on screen" is a guess at what somebody with no pointer
   * is looking at, and it is a good guess on a phone, where scrolling is the
   * only way to move. On a television it is simply wrong: there IS a pointer of
   * sorts — the focus the viewer moved there themselves — and two answers on one
   * screen means two lifted tiles, the rim on one card and the trailer in
   * another. The keyboard answer wins because it is not a guess.
   *
   * Deliberately not "if a keyboard is attached": it is the first ARROW key
   * that flips this, so a phone with a Bluetooth keyboard that nobody has
   * touched still gets the leftmost-card behaviour it was designed around.
   */
  if (remoteDriving()) return;
  /* A tapped card holds its focus while it is still meaningfully on screen —
     see `focusByPress`. Only once it has mostly left does the ambient answer
     take over again. */
  if (pressedId !== null) {
    const pressed = nodes.get(pressedId);
    if (pressed && pressed.isConnected && typeof window !== 'undefined') {
      const r = pressed.getBoundingClientRect();
      const held = { id: pressedId, top: r.top, bottom: r.bottom, left: r.left, right: r.right };
      if (pressStands(held, { width: window.innerWidth, height: window.innerHeight })) return;
    }
    pressedId = null;
  }
  if (nodes.size === 0) {
    write(null, 'centre');
    return;
  }
  const rects: FocusRect[] = [];
  const pruned = visIO !== null && visIO !== undefined;
  for (const [id, node] of nodes) {
    // Nowhere near the glass: cannot pass the visibility floors, so its rect
    // is not worth reading — except the incumbent's, whose HOLD test must be
    // allowed to see it leave. See `onGlass`.
    if (pruned && !onGlass.has(id) && id !== focusState.id) continue;
    // A node that has been detached without its action running (a keyed each
    // block being torn down mid-scroll) must not be measured.
    if (!node.isConnected) continue;
    const r = node.getBoundingClientRect();
    rects.push({ id, top: r.top, bottom: r.bottom, left: r.left, right: r.right });
  }
  const view = { width: window.innerWidth, height: window.innerHeight };
  write(leftmostId(rects, view, focusState.id), 'centre');
}

/**
 * THE RAIL FINISHES WHAT THE SWIPE STARTED. A swipe and a half settles with
 * the focused tile straddling the left edge — the visibility floors that keep
 * the answer steady DURING a scroll (`TAKE_VISIBLE`, `HOLD_VISIBLE`) permit a
 * partly-clipped tile, and at rest that is a lifted, detailed card you cannot
 * fully see. Moving the FOCUS instead would be the settle-time flicker the
 * hysteresis exists to prevent, so the track moves: the tile is brought to
 * the scroller's own snap padding, exactly where a completed snap would have
 * left it (`settleNudge`, focus-rules.ts).
 *
 * Only at settle — never mid-gesture, so it cannot fight a finger or a fling
 * — and only in centre mode, where this watcher runs at all. The nudge's own
 * scroll events re-enter `onMove` and the next settle finds a delta of 0.
 * "Never mid-gesture" is enforced, not assumed: the settle itself defers
 * while any pointer is down (`heldPointers`), because a finger pausing
 * mid-drag for SETTLE_MS used to count as the screen coming to rest.
 *
 * A MICROTASK AFTER `recompute`, because every focus write is itself deferred
 * by one (see `write`): read `focusState.id` synchronously here and the nudge
 * would aim at the tile the settle just moved away from. Microtasks are FIFO,
 * so by the time this one runs the write has landed.
 */
function nudgeFocusedIntoView(): void {
  if (typeof window === 'undefined' || remoteDriving()) return;
  const id = focusState.id;
  if (id === null) return;
  const node = nodes.get(id);
  if (!node || !node.isConnected) return;
  nudgeNode(node);
}

/**
 * The nearest horizontal scroller — a rail's track. A banner or a grid tile
 * has none, and the page itself must never be scrolled sideways by a nudge.
 * Exported for Poster's off-glass collapse compensation, which must adjust
 * exactly the scroller a nudge would have used.
 */
export function railScrollerOf(node: HTMLElement): HTMLElement | null {
  let scroller: HTMLElement | null = node.parentElement;
  while (scroller !== null && scroller !== document.body) {
    const style = getComputedStyle(scroller);
    if (
      (style.overflowX === 'auto' || style.overflowX === 'scroll') &&
      scroller.scrollWidth > scroller.clientWidth + 1
    ) {
      return scroller;
    }
    scroller = scroller.parentElement;
  }
  return null;
}

/**
 * Smooth, because a nudge is the snap finishing and a teleport reads as a
 * glitch — except where motion is refused, and on a television, where every
 * scroll is instant by design (`dpad.ts`: the SoC cannot paint an easing and
 * take the next press).
 */
function instantScroll(): boolean {
  return (
    document.documentElement.dataset['tv'] === 'on' ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  );
}

/** One nudge: bring this node wholly onto the glass, `settleNudge` arithmetic. */
function nudgeNode(node: HTMLElement): void {
  const scroller = railScrollerOf(node);
  if (scroller === null) return;
  const pad = Number.parseFloat(getComputedStyle(scroller).scrollPaddingInlineStart) || 0;
  const r = node.getBoundingClientRect();
  const delta = settleNudge({ left: r.left, right: r.right }, window.innerWidth, pad);
  if (Math.abs(delta) < 1) return;
  scroller.scrollBy({ left: delta, behavior: instantScroll() ? 'auto' : 'smooth' });
}

/**
 * THE EXPANSION FINISHES ON THE GLASS. `settleNudge` guarantees the RESTING
 * tile's visibility at scroll-settle; this is the other half of that promise,
 * for the moment the focused tile GROWS into its open 16:9 card. At a rail's
 * end the card expands rightward past the viewport and — with no scroll ever
 * fired — no settle runs to correct it: measured at 390×844, the last tile's
 * open card put 204px of itself past the right edge and the trailer played
 * half off the screen until the user dragged it back themselves.
 *
 * So the OPEN tile is watched: a ResizeObserver re-aims the same `settleNudge`
 * arithmetic (rAF-throttled, exactly the numeral-slide watcher's pattern in
 * Poster.svelte) on every width change — the 240ms flex-basis growth, a
 * trailer reveal, a viewport resize — and the final aim, taken at the settled
 * size, lands the whole card inside the padded viewport. The clamp on
 * `--tile-open` (Rail.svelte) is what makes that landing exact rather than
 * oscillatory: the open card is never wider than the glass minus both pads, so
 * `settleNudge`'s left-edge cap and its right-edge overflow agree on one
 * resting place, and the sub-pixel guard in `nudgeNode` stops the loop there.
 *
 * NEVER AGAINST A FINGER. While any pointer is down the aims are deferred —
 * a drag must not have the rail pulled back out of its hand — and re-taken on
 * release; a fling after that release ends in a settle, where the ordinary
 * settle nudge (same arithmetic, same conventions) finishes the job. Smooth
 * where scrolls are smooth, instant on TV and under reduced motion
 * (`instantScroll`), like every nudge in this file.
 *
 * Deliberately NOT gated on `remoteDriving()`: the d-pad's own placement
 * happens at the keypress, seconds before a dwell opens the card, and it has
 * no answer for the growth — on a television this is the only thing that
 * brings the expanded card fully inside the safe area, and it does so
 * instantly, which cannot fight a press.
 */
export function watchOpenTile(node: HTMLElement): () => void {
  if (typeof window === 'undefined' || typeof ResizeObserver !== 'function') return () => {};

  let frame: number | null = null;
  let held = false;
  let deferred = false;

  const aim = (): void => {
    if (!node.isConnected) return;
    if (held) {
      deferred = true;
      return;
    }
    deferred = false;
    nudgeNode(node);
  };
  const queue = (): void => {
    if (frame !== null || typeof requestAnimationFrame !== 'function') return;
    frame = requestAnimationFrame(() => {
      frame = null;
      aim();
    });
  };

  const down = (): void => {
    held = true;
  };
  const up = (): void => {
    held = false;
    if (deferred) queue();
  };
  // Capture on window: the gesture that scrolls a rail starts on the track's
  // own children, and a nudge issued mid-drag is the rail fighting the hand.
  window.addEventListener('pointerdown', down, { passive: true, capture: true });
  window.addEventListener('pointerup', up, { passive: true, capture: true });
  window.addEventListener('pointercancel', up, { passive: true, capture: true });

  const ro = new ResizeObserver(queue);
  ro.observe(node);

  return () => {
    ro.disconnect();
    if (frame !== null) cancelAnimationFrame(frame);
    window.removeEventListener('pointerdown', down, true);
    window.removeEventListener('pointerup', up, true);
    window.removeEventListener('pointercancel', up, true);
  };
}

/**
 * THE POINTERS CURRENTLY DOWN, BY ID — Rail.svelte's `heldPointers` pattern,
 * for the same reason: the settle is allowed to move the rail (the nudge's
 * scrollBy, the shut compensation's scrollLeft), and a settle that fires
 * because a mid-drag finger PAUSED for `SETTLE_MS` is not the screen coming
 * to rest — it is a hand holding the rail still. Measured in the settle
 * harness: a 120ms pause inside a touch drag fired the settle and the nudge
 * pulled the rail 125px back out from under the held finger. So while any
 * pointer is down the settle stands down entirely — focus keeps tracking
 * live (`trackLive` is reads and class toggles), but the hand-over, the
 * collapse it triggers and the nudge all wait; the release re-arms one
 * settle, so the deferred work happens `SETTLE_MS` after the hand leaves.
 */
const heldPointers = new Set<number>();

function pointerDown(event: PointerEvent): void {
  heldPointers.add(event.pointerId);
}
function pointerUp(event: PointerEvent): void {
  if (!heldPointers.delete(event.pointerId)) return;
  if (heldPointers.size === 0 && watching) settle();
}

function settle(): void {
  if (settleTimer !== null) clearTimeout(settleTimer);
  settleTimer = setTimeout(() => {
    settleTimer = null;
    if (heldPointers.size > 0) return; // deferred: pointerUp re-arms
    setMoving(false);
    recompute();
    /*
     * THE GEOMETRY MOVES ONCE, THEN THE NUDGE AIMS — in that order, atomically.
     *
     * The settle is the one moment the settled focus hands over, and the
     * hand-over is what finally collapses a flung-away open card
     * (Poster.svelte keys the trailer release on `chosen` now — collapsing on
     * the LIVE focus mid-fling was the half-tile landing: the card gave back
     * (--tile-open − --col) ≈ 226px of scrollWidth while the momentum's
     * endpoint was already chosen against the wider rail, so the browser
     * landed mid-tile; measured at 390×844, every fling from an expanded tile
     * landed 78.5px off the snap grid — docs/PERF-SCROLL.md's sequel).
     *
     * The microtask below runs after the deferred focus writes (FIFO, see
     * `write`), so Svelte has the collapse pending by then; `flushSync`
     * commits it — including Poster's same-frame scrollLeft compensation —
     * BEFORE the nudge measures. Without it, the nudge's aim is queued ahead
     * of the effect flush and measures geometry the collapse is about to
     * shift; its smooth scroll would then be cancelled by the compensation
     * and re-issued a settle later, one visible false start per fling.
     */
    queueMicrotask(() => {
      flushSync();
      nudgeFocusedIntoView();
    });
  }, SETTLE_MS);
}

/**
 * A FLICK IS NOT A CHOICE — BUT IT IS NOT A CHANGE OF MIND EITHER.
 *
 * The focus stays exactly where it was; `moving` says the screen is in motion,
 * so nothing counts this as attention, and the answer is re-read once it stops.
 * Clearing the focus here is what stopped the iPhone banner from ever starting
 * a trailer: see the header, and docs/TRAILERS.md § C2.
 */
/*
 * A frame-throttled recompute, so the focus FOLLOWS the scroll instead of
 * waiting for it to stop.
 *
 * It used to only be re-read on settle, which is why a thumb dragging through a
 * rail left the lift and the description sitting on whichever card had been
 * under it when the flick began — the row moved and the attention did not, and
 * it read as lag rather than as restraint. One recompute per animation frame is
 * cheap (it is `getBoundingClientRect` over the registered cards and a pure
 * pick) and it is the ceiling that matters: without the guard, a momentum
 * scroll fires `scroll` far more often than the screen redraws.
 *
 * `moving` still goes true, and that is deliberate — it is what keeps a trailer
 * from starting mid-flick. What changed is that the focus itself no longer
 * waits: attention tracks the finger, commitment still waits for it to stop.
 */
let liveFrame: number | null = null;
function trackLive(): void {
  if (liveFrame !== null || typeof requestAnimationFrame !== 'function') return;
  liveFrame = requestAnimationFrame(() => {
    liveFrame = null;
    recompute();
  });
}

function onMove(): void {
  setMoving(true);
  trackLive();
  settle();
}

function startWatching(): void {
  if (watching || typeof window === 'undefined') return;
  watching = true;
  window.addEventListener('scroll', onMove, { passive: true, capture: true });
  window.addEventListener('resize', onMove, { passive: true });
  // Capture, like Rail's watchHold: the gestures that scroll a rail start on
  // its children, and the settle must know a hand is on the glass wherever
  // it landed.
  window.addEventListener('pointerdown', pointerDown, { passive: true, capture: true });
  window.addEventListener('pointerup', pointerUp, { passive: true, capture: true });
  window.addEventListener('pointercancel', pointerUp, { passive: true, capture: true });
  settle();
}

function stopWatching(): void {
  if (!watching || typeof window === 'undefined') return;
  watching = false;
  if (liveFrame !== null) {
    cancelAnimationFrame(liveFrame);
    liveFrame = null;
  }
  window.removeEventListener('scroll', onMove, true);
  window.removeEventListener('resize', onMove);
  window.removeEventListener('pointerdown', pointerDown, true);
  window.removeEventListener('pointerup', pointerUp, true);
  window.removeEventListener('pointercancel', pointerUp, true);
  heldPointers.clear();
  if (settleTimer !== null) clearTimeout(settleTimer);
  settleTimer = null;
  setMoving(false);
}

/* ── The action every focusable thing uses ───────────────────────────────── */

let counter = 0;

/**
 * A focus id for one INSTANCE of a card.
 *
 * Deliberately not the IMDb id: the same title legitimately appears in two
 * rails at once — "Popular films" and "Comedy" — and two cards sharing a key
 * would mean one of them silently replacing the other in the registry, a
 * trailer playing in the wrong one, and both drawing themselves lifted.
 */
export function newFocusId(prefix = 'f'): string {
  counter += 1;
  return `${prefix}-${counter}`;
}

export interface FocusableOptions {
  /** Stable across re-renders — the title's IMDb id, or `hero`. */
  id: string;
  /** Opt out entirely (a ghost card, a disabled tile). */
  enabled?: boolean;
}

/**
 * `<div use:focusable={{ id: item.id }}>` — that is the whole contract.
 *
 * Under a pointer the node's own events decide. Under a thumb the node simply
 * joins the set of things that can be centred, and the page-level watcher
 * decides. Both write the same one place.
 */
export function focusable(
  node: HTMLElement,
  options: FocusableOptions
): { update(next: FocusableOptions): void; destroy(): void } {
  let id = options.id;
  let enabled = options.enabled ?? true;

  /*
   * HOVER IS A FINE-POINTER IDEA, so a touch's synthetic enter/leave is ignored
   * PER EVENT rather than per device: on a touchscreen laptop (pointer mode all
   * afternoon) a tap fires pointerenter → pointerdown → pointerup →
   * pointerleave in one gesture — WebKit measured, the enter lands BEFORE the
   * pointerdown — and letting that enter write the focus would make the card
   * "already focused" by the time its own click asks, turning the first tap
   * into a navigation. The click path (`tapVerdict`) is what answers for
   * touches; these two answer for the mouse.
   *
   * A WHITELIST, not `!== 'touch'`: a real hover pointer always says 'mouse'
   * or 'pen' (every engine labels its true pointer events), while a synthetic
   * or compatibility event may say nothing at all — and an unlabelled enter
   * must be treated as the touch fallout it almost certainly is, because the
   * cost of honouring it is the first-tap-navigates bug and the cost of
   * ignoring it is nothing.
   */
  const enter = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    write(id, 'pointer');
  };
  const leave = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    if (focusState.id === id) write(null, 'pointer');
  };
  const focusIn = (): void => {
    write(id, 'keyboard');
    /**
     * BRING THE FOCUSED CARD FULLY INTO VIEW.
     *
     * Tabbing along a rail, the browser's own focus scrolling did not always fire —
     * measured at 1440x900, card 8 took focus at left 1376 / right 1532 against a
     * 1440 px viewport, 92 px clipped off the right edge, with the track's
     * scrollLeft still 0. Card 9 finally moved it. So one card in ten held the focus
     * while partly hidden, which is a WCAG 2.4.11 problem and simply looks broken.
     *
     * `nearest` on both axes does nothing at all when the element is already fully
     * visible, so this cannot fight the reader or undo a deliberate scroll.
     *
     * UNLESS THE D-PAD IS THE ONE MOVING THE FOCUS. `dpad.ts` has just decided
     * which direction was pressed and has its own, better-mannered answer for
     * where the card should come to rest — a peek of the next tile in a rail,
     * 42% down the page for a rail. Running both means one press produces two
     * visible movements: an instant snap to the nearest edge from here, then a
     * smooth glide to the middle from there.
     */
    if (typeof node.scrollIntoView === 'function' && !movingFocus()) {
      node.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    }
  };
  const focusOut = (event: FocusEvent): void => {
    // Moving between the card's own link and a control it carries (the
    // trailer's mute button) is not leaving it.
    const to = event.relatedTarget;
    if (to instanceof Node && node.contains(to)) return;
    // The over-video chrome is PORTAL'D into the body-level stage host while
    // a trailer shows (Poster's `overVideo`), so containment cannot see it any
    // more — and the stage only ever rides the focused card, so focus landing
    // there is focus landing on this card's own control. Without this line,
    // Tab onto the mute button collapsed the card under the keyboard.
    if (to instanceof Element && to.closest('.jt-tile-stage') !== null) return;
    if (focusState.id !== id) return;
    /**
     * UNDER A THUMB, LOSING THE FOCUS IS NOT LOSING THE ANSWER.
     *
     * These listeners are now bound on every device (see `attach`), which means
     * a phone reaches them for the first time: tapping the banner's save button
     * focuses it, and tapping anywhere else afterwards fires this. Writing
     * `null` there would blank the ambient answer — the leftmost card of the row
     * nearest the middle — until the next scroll happened to recompute it, and
     * with it every trailer dwell on the screen. So on a touch device the
     * watcher is simply asked for its answer again.
     */
    if (currentMode() === 'centre' && !remoteDriving()) {
      settle();
      return;
    }
    write(null, 'keyboard');
  };

  function attach(): void {
    if (!enabled) return;
    nodes.set(id, node);
    const io = visObserver();
    if (io !== null) {
      visIds.set(node, id);
      io.observe(node);
    }
    /**
     * KEYBOARD FOCUS COUNTS ON EVERY DEVICE, and it used to count only where a
     * fine pointer was reported.
     *
     * That was right when the only two inputs were a mouse and a thumb. A
     * television is neither: an Android TV WebView reports `pointer: coarse` and
     * `hover: none` exactly like a phone, so a Fire TV Stick took the thumb
     * branch, never bound these, and a card the viewer had explicitly focused
     * with the remote was not the card drawn lifted — the leftmost one on screen
     * was. The listeners are harmless under a real thumb, where nothing ever
     * fires them; the ambient watcher is what decides there, and it goes on
     * deciding until the first arrow key (see `recompute`).
     */
    node.addEventListener('focusin', focusIn);
    node.addEventListener('focusout', focusOut);
    if (currentMode() === 'pointer') {
      node.addEventListener('pointerenter', enter);
      node.addEventListener('pointerleave', leave);
    } else {
      startWatching();
      settle();
    }
  }

  function detach(): void {
    if (nodes.get(id) === node) nodes.delete(id);
    if (visIO) {
      visIO.unobserve(node);
      visIds.delete(node);
      onGlass.delete(id);
    }
    node.removeEventListener('pointerenter', enter);
    node.removeEventListener('pointerleave', leave);
    node.removeEventListener('focusin', focusIn);
    node.removeEventListener('focusout', focusOut);
    if (focusState.id === id) write(null, focusState.source);
    if (nodes.size === 0) stopWatching();
  }

  attach();

  return {
    update(next: FocusableOptions): void {
      const changed = next.id !== id || (next.enabled ?? true) !== enabled;
      if (!changed) return;
      detach();
      id = next.id;
      enabled = next.enabled ?? true;
      attach();
    },
    destroy(): void {
      detach();
    }
  };
}

/** Tests only: forget the page-level state between cases. */
export function resetFocusForTests(): void {
  nodes.clear();
  onGlass.clear();
  if (visIO) visIO.disconnect();
  visIO = undefined;
  stopWatching();
  focusState.id = null;
  focusState.moving = false;
  settledFocus.id = null;
  mode = null;
  pressedId = null;
}
